import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import "./PlaceOrder.css";
import {BASE_URL} from "./Constants";

const PlaceOrder = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [shippingMethod, setShippingMethod] = useState("");
  const [shippingModes, setShippingModes] = useState([]);
  const [shippingCost, setShippingCost] = useState(0);
  const [basePrice, setBasePrice] = useState(0);
  const [gstPercentage, setGstPercentage] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [gstId, setGstId] = useState(null);
  const [CartId, setCartId] = useState(null);
  const [, setOrderId] = useState(null);

  // -------------------- Fetch Logged-in User --------------------
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/get_user_details`, {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Not logged in");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.log("No user logged in:", err.message);
        navigate("/checkout/login");
      }
    };
    fetchUser();
  }, [navigate]);

  // -------------------- Fetch Shipping Modes --------------------
  useEffect(() => {
      const fetchShippingModes = async () => {
        try {
          const res = await fetch(`${BASE_URL}/fetch_shipping_modes`, {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            }
          });
          if (!res.ok) throw new Error("Failed to fetch shipping modes");
          const data = await res.json();
          setShippingModes(data.shipping_modes || []);
        } catch (err) {
          console.error("Error fetching shipping modes:", err.message);
        }
      };

    fetchShippingModes();
  }, []);

  // -------------------- Fetch Cart Items --------------------
  const fetchCartItems = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(
        `${BASE_URL}/cart/fetch_cart_details?user_id=${user.user_id}`,
        { method: "GET", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch cart");
      const data = await res.json();

      if (data.cart_id) setCartId(data.cart_id);

      const updatedItems = (data.items || []).map((item) => {
        const price =
          typeof item.price === "string"
            ? parseFloat(item.price.replace(/[^\d.-]/g, ""))
            : item.price || 0;
        const quantity = item.quantity || 1;
        const gstPercentage =
          item.gst !== undefined ? item.gst : item.gst_percentage || 0;
        const gstAmount = price * quantity * (gstPercentage / 100);
        return {
          ...item,
          price,
          gst_percentage: gstPercentage,
          gst_amount: gstAmount,
        };
      });

      setCartItems(updatedItems);
    } catch (err) {
      // console.error("Error fetching cart:", err);
      setCartItems([]);
    }
  }, [user]);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  const calculateShipping = async (method) => {

    if (!CartId) return;

    try {

      const res = await fetch(`${BASE_URL}/calculate_order_shipping_Modes_charges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          cart_id: CartId,
          shipping_type: method,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Shipping calculation failed");
      }

      const shippingData = await res.json();

      const base = shippingData.Base_Price || shippingData.base_price || 0;
      const gstPerc = shippingData.Gst_Percentage || shippingData.gst_percentage || 0;
      const gstVal = shippingData.Gst_price || shippingData.gst_price || 0;
      const totalShip = shippingData.Subtotal || shippingData.subtotal || base + gstVal;

      setBasePrice(base);
      setGstPercentage(gstPerc);
      setGstAmount(gstVal);
      setShippingCost(totalShip);
      setGstId(shippingData.gst_id || null);

    } catch (err) {
      console.error("Shipping calculation error:", err);
    }
  };


  // -------------------- Totals Calculation --------------------
  const calculateTotals = () => {
    let subtotal = 0;
    let gstTotal = 0;
    cartItems.forEach((item) => {
      subtotal += item.price * item.quantity;
      gstTotal += item.gst_amount || 0;
    });
    const grandTotal = subtotal + gstTotal + shippingCost;
    return { subtotal, gstTotal, grandTotal };
  };

  const { subtotal, gstTotal, grandTotal } = calculateTotals();

  // -------------------- Place Order (with Shipping Calculation) --------------------
  const handlePlaceOrder = async () => {
    if (!user) {
      alert("Please login first");
      navigate("/checkout/login");
      return;
    }
    if (!cartItems.length) {
      alert("Your cart is empty");
      return;
    }
    if (!shippingMethod) {
      alert("Please select a shipping method first");
      return;
    }

    try {
      // 1️⃣ Create order first
      const createOrderRes = await fetch(`${BASE_URL}/create_order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: user.user_id,
          shipping: shippingMethod,
          items: cartItems.map((item) => ({
            item_id: item.item_id,
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            gst_percentage: item.gst_percentage,
            gst_amount: item.gst_amount,
          })),
        }),
      });

      if (!createOrderRes.ok)
        throw new Error("Failed to create order before shipping calculation");

      const orderData = await createOrderRes.json();
      const newOrderId = orderData.order_id;
      setOrderId(newOrderId);

      // 3️⃣ Save shipping charges to DB
      const saveRes = await fetch(`${BASE_URL}/save_charges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          order_id: newOrderId,
          type: shippingMethod,
          gst_id: gstId,
        }),
      });

      const saveData = await saveRes.json();
      if (!saveRes.ok)
        throw new Error(saveData.error || "Failed to save shipping");

      // 4️⃣ Clear cart after success
      if (CartId) {
        await fetch(`${BASE_URL}/cart_items/clear_cart/${CartId}`, {
          method: "DELETE",
          credentials: "include",
        });
        setCartItems([]);
      }

      // 5️⃣ Navigate to payment
      window.dispatchEvent(new Event("cartUpdated"));
      navigate(`/payment?orderId=${newOrderId}`);
    } catch (err) {
      alert("Failed to place order. Please try again.");
    }
  };

  // -------------------- UI --------------------
  return (
    <>
      <div className="place-order-container">
        <div className="order-box">
          <h2>Your Order</h2>
          <table className="order-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Title</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>GST %</th>
                <th>GST Amount</th>
                <th>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Your cart is empty
                  </td>
                </tr>
              ) : (
                <>
                  {cartItems.map((item) => (
                    <tr key={item.cart_item_id}>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/Product/${item.item_id}`)}
                      >
                        {item.image_id ? (
                          <img
                            src={
                              item.image_id
                                ? `${BASE_URL}/download_image/${item.image_id}`
                                : "/assets/default.jpg"
                            }
                            alt={item.name}
                            style={{ width: "60px", height: "60px", objectFit: "cover" }}
                          />
                        ) : (
                          "No image"
                        )}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>₹ {item.price.toFixed(2)}</td>
                      <td>{item.gst_percentage.toFixed(2)}%</td>
                      <td>₹ {item.gst_amount.toFixed(2)}</td>
                      <td>₹ {(item.price * item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}

                  {shippingCost > 0 && (
                    <tr className="shipping-row">
                      <td>🚚</td>
                      <td>
                        <strong>Shipping ({shippingMethod})</strong>
                      </td>
                      <td>1</td>
                      <td>₹ {basePrice.toFixed(2)}</td>
                      <td>{gstPercentage.toFixed(2)}%</td>
                      <td>₹ {gstAmount.toFixed(2)}</td>
                      <td>
                        <strong>₹ {shippingCost.toFixed(2)}</strong>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>

          {/* ✅ Shipping Options */}
          <div className="shipping-section">
            <h3>Shipping</h3>
            {shippingModes.length > 0 ? (
              shippingModes.map((mode) => (
                <label key={mode.id} className="shipping-option">
                  <input
                    type="radio"
                    name="shipping"
                    value={mode.type}
                    checked={shippingMethod === mode.type}
                    onChange={(e) => {
                      const method = e.target.value;
                      setShippingMethod(method);
                      calculateShipping(method);
                    }
                    }
                  />
                  <span>
                    <strong>{mode.type}</strong><br/>
                    <strong>{mode.base_price}</strong> +  <strong>{mode.gst_percentage}%</strong> 
                  </span>
                  <div>{mode.delivery_info}</div>
                </label>
              ))
            ) : (
              <p>Loading shipping options...</p>
            )}
          </div>

          {/* Totals */}
          <div className="total-section">
            <p>
              <strong>Subtotal:</strong> ₹ {subtotal.toFixed(2)}
            </p>
            <p>
              <strong>GST Amount:</strong> ₹ {gstTotal.toFixed(2)}
            </p>
            <p>
              <strong>Shipping:</strong> ₹ {shippingCost.toFixed(2)}
            </p>
            <hr />
            <p className="total">
              <strong>Total:</strong> ₹ {grandTotal.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="payment-box">
          <h2>Payment Method</h2>
          <label>
            <p>
              Once order is placed, it will be reviewed and the payment options will be shared 
              to your registered email or whatsapp 
            </p>
          </label>
          <button className="place-order-button" onClick={handlePlaceOrder}>
            Place Order
          </button>
          <hr/>
          <div className="privacy-policy">
            <p>
              Your personal data will be used to process your order, support your
              experience, and for other purposes described in our{" "}
              <span>privacy policy</span>.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default PlaceOrder;